/**
 * Lock Persistence Manager
 * 
 * Manages lock persistence across internal navigation using sessionStorage.
 * Locks are cleared only when the browser tab closes.
 * Includes a global heartbeat system to keep all persisted locks alive.
 */

export interface PersistedLock {
  itemId: string;
  itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow';
  timestamp: number;
  gameId: string;
}

// Heartbeat interval - 30 seconds (same as useItemLock)
const GLOBAL_HEARTBEAT_INTERVAL_MS = 30 * 1000;

class LockPersistenceManager {
  private storageKey = 'locanest_active_locks';
  private globalHeartbeatInterval: NodeJS.Timeout | null = null;
  private isHeartbeatRunning = false;

  /**
   * Store a lock for persistence and start global heartbeat if needed
   */
  storeLock(itemId: string, itemType: string, gameId: string): void {
    try {
      const locks = this.getStoredLocks();
      
      // Remove existing entry for this item if present
      const filtered = locks.filter(
        lock => !(lock.itemId === itemId && lock.itemType === itemType)
      );
      
      // Add new entry
      filtered.push({
        itemId,
        itemType: itemType as PersistedLock['itemType'],
        timestamp: Date.now(),
        gameId,
      });
      
      sessionStorage.setItem(this.storageKey, JSON.stringify(filtered));
      
      // Start global heartbeat to keep all persisted locks alive
      this.startGlobalHeartbeat();
    } catch (error) {
      console.error('[LockPersistence] Failed to store lock:', error);
    }
  }

  /**
   * Remove a lock from persistence and stop heartbeat if no locks remain
   */
  removeLock(itemId: string, itemType: string): void {
    try {
      const locks = this.getStoredLocks();
      const filtered = locks.filter(
        lock => !(lock.itemId === itemId && lock.itemType === itemType)
      );
      
      sessionStorage.setItem(this.storageKey, JSON.stringify(filtered));
      
      // Stop global heartbeat if no locks remain
      if (filtered.length === 0) {
        this.stopGlobalHeartbeat();
      }
    } catch (error) {
      console.error('[LockPersistence] Failed to remove lock:', error);
    }
  }

  /**
   * Get all stored locks
   */
  getStoredLocks(): PersistedLock[] {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (!stored) return [];
      
      const locks = JSON.parse(stored) as PersistedLock[];
      
      // Filter out stale locks (older than 10 minutes)
      const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
      const fresh = locks.filter(lock => lock.timestamp > tenMinutesAgo);
      
      // Update storage if we filtered any out
      if (fresh.length !== locks.length) {
        sessionStorage.setItem(this.storageKey, JSON.stringify(fresh));
      }
      
      return fresh;
    } catch (error) {
      console.error('[LockPersistence] Failed to get stored locks:', error);
      return [];
    }
  }

  /**
   * Check if a lock is stored
   */
  hasStoredLock(itemId: string, itemType: string): boolean {
    const locks = this.getStoredLocks();
    return locks.some(lock => lock.itemId === itemId && lock.itemType === itemType);
  }

  /**
   * Get a specific stored lock
   */
  getStoredLock(itemId: string, itemType: string): PersistedLock | null {
    const locks = this.getStoredLocks();
    return locks.find(lock => lock.itemId === itemId && lock.itemType === itemType) || null;
  }

  /**
   * Clear all stored locks and stop heartbeat
   */
  clearAll(): void {
    try {
      sessionStorage.removeItem(this.storageKey);
      this.stopGlobalHeartbeat();
    } catch (error) {
      console.error('[LockPersistence] Failed to clear locks:', error);
    }
  }

  /**
   * Get count of stored locks
   */
  getCount(): number {
    return this.getStoredLocks().length;
  }

  /**
   * Start global heartbeat to keep all persisted locks alive
   */
  private startGlobalHeartbeat(): void {
    if (this.isHeartbeatRunning) {
      return; // Already running
    }

    this.isHeartbeatRunning = true;

    // Import itemLockStore dynamically to avoid circular dependency
    import('@/lib/stores/itemLockStore').then(({ itemLockStore }) => {
      this.globalHeartbeatInterval = setInterval(async () => {
        const locks = this.getStoredLocks();
        
        if (locks.length === 0) {
          this.stopGlobalHeartbeat();
          return;
        }


        // Refresh all persisted locks
        for (const lock of locks) {
          try {
            const success = await itemLockStore.refreshLock(lock.itemId, lock.itemType);
            if (!success) {
              console.warn(`[LockPersistence] Failed to refresh lock for ${lock.itemType}:${lock.itemId} - removing from persistence`);
              // Lock no longer exists on server, remove from persistence
              this.removeLock(lock.itemId, lock.itemType);
            }
          } catch (error) {
            console.error(`[LockPersistence] Error refreshing lock for ${lock.itemType}:${lock.itemId}:`, error);
          }
        }
      }, GLOBAL_HEARTBEAT_INTERVAL_MS);
    });
  }

  /**
   * Stop global heartbeat
   */
  private stopGlobalHeartbeat(): void {
    if (this.globalHeartbeatInterval) {
      clearInterval(this.globalHeartbeatInterval);
      this.globalHeartbeatInterval = null;
      this.isHeartbeatRunning = false;
    }
  }
}

// Singleton instance
export const lockPersistenceManager = new LockPersistenceManager();
export default lockPersistenceManager;
